// calibrationLogBookSocketService.js
// Centralized Socket.IO handling for the standalone Calibration Log Book realtime sync.
// Mirrors samplingEventSocketService.js (no JSA channel).

import { io } from "socket.io-client";

let socket = null;
let updateHandler = null; // function(patch) => void - for incoming broadcasts
let connectHandler = null; // function(connected:boolean) => void
let joinAckHandler = null; // function(ack) => void
let visibilityHandler = null; // prevents Heroku H15 idle connection errors on iPad

const BACKEND_URL =
  process.env.REACT_APP_SOCKET_URL ||
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:5000"
    : window.location.origin);

export const initializeCalibrationLogBookSocket = () => {
  if (socket) return socket;
  socket = io(BACKEND_URL, {
    withCredentials: true,
    transports: ["websocket", "polling"],
    path: "/socket.io",
    reconnectionAttempts: 10,
    reconnectionDelay: 5000,
    reconnectionDelayMax: 30000,
    randomizationFactor: 0.5,
  });

  socket.on("connect", () => {
    if (typeof connectHandler === "function") connectHandler(true);
  });
  socket.on("disconnect", () => {
    if (typeof connectHandler === "function") connectHandler(false);
  });
  // Listen for incoming broadcasts from server
  socket.on("calibration_log_book_update", (patchData) => {
    if (typeof updateHandler === "function") updateHandler(patchData);
  });

  visibilityHandler = () => {
    if (!socket) return;
    if (document.hidden) {
      socket.disconnect();
    } else {
      socket.connect();
    }
  };
  document.addEventListener("visibilitychange", visibilityHandler);

  return socket;
};

// Register handler for incoming broadcasts (routes to store's applyWebsocketPatch)
export const onCalibrationLogBookUpdate = (fn) => {
  updateHandler = typeof fn === "function" ? fn : null;
};

// Register handler for connection status changes
export const onCalibrationLogBookConnectionChange = (fn) => {
  connectHandler = typeof fn === "function" ? fn : null;
  if (connectHandler && socket) {
    try {
      connectHandler(Boolean(socket.connected));
    } catch {}
  }
};

// Optional handler to consume join ACK payloads (e.g., to hydrate book)
export const onCalibrationLogBookJoinAck = (fn) => {
  joinAckHandler = typeof fn === "function" ? fn : null;
};

// Clear all consumer handlers to avoid dangling callbacks after unmount
export const clearCalibrationLogBookHandlers = () => {
  updateHandler = null;
  connectHandler = null;
  joinAckHandler = null;
};

// Check if socket is connected
export const isCalibrationLogBookSocketConnected = () => {
  return Boolean(socket && socket.connected);
};

// Join a calibration log book room
export const joinCalibrationLogBook = (bookId) =>
  new Promise((resolve) => {
    if (!socket) initializeCalibrationLogBookSocket();
    if (!socket)
      return resolve({ success: false, error: "socket_init_failed" });
    socket.emit(
      "join_calibration_log_book",
      { calibration_log_book_id: bookId },
      (ack) => {
        try {
          if (typeof joinAckHandler === "function") joinAckHandler(ack);
        } catch {}
        resolve(ack || { success: false });
      }
    );
  });

// Leave a calibration log book room
export const leaveCalibrationLogBook = (bookId) =>
  new Promise((resolve) => {
    if (!socket) return resolve({ success: true });
    socket.emit(
      "leave_calibration_log_book",
      { calibration_log_book_id: bookId },
      (ack) => resolve(ack || { success: true })
    );
  });

/**
 * Emit a patch to the server via websocket.
 * Returns { sent: boolean, shouldQueue: boolean, error: string|null }
 */
export const emitCalibrationLogBookPatch = (bookId, patch) =>
  new Promise((resolve) => {
    if (!socket || !socket.connected) {
      return resolve({ sent: false, shouldQueue: true, error: null });
    }

    // Inject the parent id so the row patches don't have to carry it.
    const payload = { calibration_log_book_id: bookId, ...patch };

    const timeout = setTimeout(() => {
      resolve({ sent: true, shouldQueue: true, error: "timeout" });
    }, 10000);

    socket.emit("autosave_calibration_log_book", payload, (ack) => {
      clearTimeout(timeout);
      if (!ack) {
        resolve({ sent: true, shouldQueue: true, error: "no_response" });
      } else if (ack.success) {
        resolve({ sent: true, shouldQueue: false, error: null });
      } else {
        resolve({ sent: true, shouldQueue: false, error: ack.error });
      }
    });
  });

// Disconnect socket and clean up
export const disconnectCalibrationLogBookSocket = () => {
  if (socket) {
    try {
      socket.off("calibration_log_book_update");
      socket.off("connect");
      socket.off("disconnect");
    } catch {}
    if (visibilityHandler) {
      document.removeEventListener("visibilitychange", visibilityHandler);
      visibilityHandler = null;
    }
    clearCalibrationLogBookHandlers();
    socket.disconnect();
    socket = null;
  }
};
